import { useState, type ReactNode } from 'react';
import { CircleCheckIcon, PackageXIcon, TriangleAlertIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { ReturnOrderDiscrepancyStatus } from '@/graphql/generatedTypes';
import i18n, { getDateFormatOptions } from '@/utils/i18n';

import type { OpsOrderDetail } from './types';

type ReturnFollowUp = NonNullable<OpsOrderDetail['return_follow_up']>;

interface ReturnFollowUpCardProps {
  followUp: ReturnFollowUp;
  onResolve?: (note: string) => Promise<boolean>;
  resolving: boolean;
}

export function ReturnFollowUpCard({
  followUp,
  onResolve,
  resolving,
}: ReturnFollowUpCardProps) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const status = followUp.discrepancy_status;
  const parcelLost = !!followUp.parcel_lost_at;
  if (!status && !parcelLost) return null;

  const resolutionOwner: FollowUpKind = parcelLost
    ? 'parcelLost'
    : 'discrepancy';
  const texts = FOLLOW_UP_TEXTS[resolutionOwner];
  const resolveButton = onResolve && (
    <Button size="sm" variant="outline" onClick={() => setResolveOpen(true)}>
      {i18n.t(texts.action)}
    </Button>
  );
  const resolution = followUp.resolved_at && (
    <>
      <p>
        {i18n.t(texts.resolvedBy, {
          user: followUp.resolved_by ?? '–',
          date: formatDate(followUp.resolved_at),
        })}
      </p>
      {followUp.resolution_note && (
        <p className="tw:text-foreground tw:whitespace-pre-wrap">
          {followUp.resolution_note}
        </p>
      )}
    </>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{i18n.t('Order:sectionFollowUp')}</CardTitle>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-3">
        {followUp.parcel_lost_at && (
          <FollowUpAlert
            icon={<PackageXIcon />}
            title={i18n.t('Order:parcelLostTitle')}
            open={!followUp.resolved_at}
            badge={i18n.t(
              followUp.resolved_at
                ? 'Order:parcelLostAcknowledged'
                : 'Order:parcelLostOpenBadge',
            )}
            action={resolutionOwner === 'parcelLost' && resolveButton}
          >
            <p>
              {i18n.t('Order:parcelLostDetail', {
                count: followUp.lost_container_count,
                date: formatDate(followUp.parcel_lost_at),
                user: followUp.parcel_lost_by ?? '–',
              })}
            </p>
            {resolutionOwner === 'parcelLost' && resolution}
          </FollowUpAlert>
        )}

        {status && (
          <FollowUpAlert
            icon={<TriangleAlertIcon />}
            title={i18n.t('Order:discrepancyLineCount', {
              count: followUp.discrepancy_lines.length,
            })}
            open={status === ReturnOrderDiscrepancyStatus.Open}
            badge={i18n.t(
              status === ReturnOrderDiscrepancyStatus.Open
                ? 'Order:discrepancyOpen'
                : 'Order:discrepancyResolved',
            )}
            action={resolutionOwner === 'discrepancy' && resolveButton}
          >
            <p>
              {i18n.t('Order:discrepancySeeIntake')}
              {' · '}
              {followUp.discrepancy_mail_sent_at
                ? i18n.t('Order:discrepancyMailSent', {
                    date: formatDate(followUp.discrepancy_mail_sent_at),
                  })
                : i18n.t('Order:discrepancyMailNotSent')}
            </p>
            {resolutionOwner === 'discrepancy' && resolution}
          </FollowUpAlert>
        )}
      </CardContent>

      {onResolve && (
        <ResolveFollowUpDialog
          open={resolveOpen}
          onOpenChange={setResolveOpen}
          onResolve={onResolve}
          resolving={resolving}
          texts={texts}
        />
      )}
    </Card>
  );
}

function FollowUpAlert({
  icon,
  title,
  open,
  badge,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  open: boolean;
  badge: string;
  action: ReactNode;
  children: ReactNode;
}) {
  return (
    <Alert variant={open ? 'warning' : 'default'}>
      {open ? icon : <CircleCheckIcon />}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-2">
        <div className="tw:flex tw:flex-col tw:gap-0.5">
          <AlertTitle className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            {title}
            <Badge variant={open ? 'warning' : 'secondary'}>{badge}</Badge>
          </AlertTitle>
          <AlertDescription className="tw:flex tw:flex-col tw:gap-1 tw:[&_p:not(:last-child)]:mb-0">
            {children}
          </AlertDescription>
        </div>
        {open && action}
      </div>
    </Alert>
  );
}

interface FollowUpTexts {
  action: string;
  title: string;
  description: string;
  notePlaceholder: string;
  confirm: string;
  resolvedBy: string;
}

type FollowUpKind = 'discrepancy' | 'parcelLost';

const FOLLOW_UP_TEXTS: Record<FollowUpKind, FollowUpTexts> = {
  discrepancy: {
    action: 'Order:discrepancyResolveAction',
    title: 'Order:discrepancyResolveTitle',
    description: 'Order:discrepancyResolveDescription',
    notePlaceholder: 'Order:discrepancyResolutionNotePlaceholder',
    confirm: 'Order:discrepancyResolveConfirm',
    resolvedBy: 'Order:discrepancyResolvedBy',
  },
  parcelLost: {
    action: 'Order:parcelLostAcknowledgeAction',
    title: 'Order:parcelLostAcknowledgeTitle',
    description: 'Order:parcelLostAcknowledgeDescription',
    notePlaceholder: 'Order:parcelLostAcknowledgeNotePlaceholder',
    confirm: 'Order:parcelLostAcknowledgeConfirm',
    resolvedBy: 'Order:parcelLostAcknowledgedBy',
  },
};

function ResolveFollowUpDialog({
  open,
  onOpenChange,
  onResolve,
  resolving,
  texts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolve: (note: string) => Promise<boolean>;
  resolving: boolean;
  texts: FollowUpTexts;
}) {
  const [note, setNote] = useState('');
  const trimmed = note.trim();

  const handleOpenChange = (next: boolean) => {
    if (!next) setNote('');
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (await onResolve(trimmed)) handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t(texts.title)}</DialogTitle>
          <DialogDescription>{i18n.t(texts.description)}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field>
            <FieldLabel htmlFor="follow-up-resolution-note">
              {i18n.t('Order:discrepancyResolutionNote')}
            </FieldLabel>
            <Textarea
              id="follow-up-resolution-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="tw:min-h-24 tw:resize-none"
              placeholder={i18n.t(texts.notePlaceholder)}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={resolving}>
              {i18n.t('Common:cancel')}
            </Button>
          </DialogClose>
          <Button onClick={handleConfirm} disabled={!trimmed || resolving}>
            {resolving && <Spinner />}
            {i18n.t(texts.confirm)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(
    i18n.language,
    getDateFormatOptions(true),
  );
}
